import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { getFiGrowthBook } from '../../lib/growthbook';
import { DEV_MODE, getCookieDomain, removeCookie } from '../../lib/util';
import {
  getNanoVariantPetGenderSelect,
  getNanoVariantPetName,
  hasNanoVariantPetName,
} from '../../lib/util/petUrlParams';
import styles from './RescueBanner.module.scss';

export const RESCUE_BANNER_COOKIE = 'rescue_banner_data';
export const RESCUE_BANNER_DISMISSED_COOKIE = 'rescue_banner_dismissed';
export const RESCUE_BANNER_COOKIE_EXPIRATION_DAYS = 7;
export const RESCUE_GWA_PDP_FLAG = 'ecom-rescue-gwa-pdp';

interface RescueBannerData {
  orgName: string | null;
  offer: string;
}

/**
 * Kill switch for the whole adoption experience. Every rescue surface - banner, offer box, chip
 * module, testimonials, faq, the suppressed promo field, the cart's renewal and discount rows -
 * reads `showRescueGwaVariant()`, so turning this off reverts all of them together rather than
 * leaving a half-rescue page.
 *
 * Defaults to on: the flag exists to switch a fully-rolled-out feature off in an incident, so a
 * GrowthBook fetch that times out (3s, see `growthbook.ts`) must not take the adoption offer down
 * with it. The consequence is that flipping it off does not reach a shopper whose features failed
 * to load - acceptable for a rare client-side failure, but it does mean this is not a security
 * boundary. Entry is still governed by the signed entitlement, which is server-side.
 */
export function rescueGwaPdpEnabled(): boolean {
  return getFiGrowthBook().getFeatureValue<boolean>(RESCUE_GWA_PDP_FLAG, true);
}

export function showRescueGwaVariant(): boolean {
  return rescueGwaPdpEnabled() && !!Cookies.get(RESCUE_BANNER_COOKIE);
}

export function rescueBannerDismissed(): boolean {
  return !!Cookies.get(RESCUE_BANNER_DISMISSED_COOKIE);
}

/**
 * records the dismissal in its own cookie and leaves `rescue_banner_data` in place. that cookie is
 * also the rescue-variant gate (`showRescueGwaVariant`), so clearing it here would turn off the
 * whole rescue experience for the session, not just this banner. scoped like the other shop cookies
 * so the dismissal holds across subdomains, and expires with the rescue context it belongs to.
 */
export function dismissRescueBanner() {
  Cookies.set(RESCUE_BANNER_DISMISSED_COOKIE, '1', {
    path: '/',
    expires: RESCUE_BANNER_COOKIE_EXPIRATION_DAYS,
    domain: getCookieDomain(),
    secure: !DEV_MODE,
  });
}

/**
 * `removeCookie` rather than `Cookies.remove`: the dismissal cookie is written against the shared
 * parent domain, and a removal that omits that domain silently does nothing in production.
 */
export function clearRescueBannerDismissal() {
  removeCookie(RESCUE_BANNER_DISMISSED_COOKIE);
}

/**
 * Reads rescue banner data from a cookie set by the /rescue/:slug landing route.
 * Renders the co-brand header block on the shop page: eyebrow naming the rescue, headline stating
 * the offer. Re-checks the cookie on every navigation so it picks up the value after redirect.
 *
 * Deliberately not dismissible: the rescue connection is the whole reason this traffic converts,
 * and dismissal used to read as "turn the rescue experience off". The dismissal cookie helpers
 * above stay exported for the landing route and existing tests, but nothing consults them here.
 */
export default function RescueBanner() {
  const location = useLocation();
  const { t } = useTranslation();
  const [bannerData, setBannerData] = useState<RescueBannerData | null>(null);

  useEffect(() => {
    const raw = Cookies.get(RESCUE_BANNER_COOKIE);
    if (raw) {
      try {
        setBannerData(JSON.parse(raw));
      } catch {
        // ignore malformed cookie
      }
    }
  }, [location.pathname]);

  if (!bannerData) {
    return null;
  }

  // a program with no org name gets the generic partner eyebrow, never "Your rescue". the headline
  // is shared: it states the offer as an attribute of the adoption, so no payer has to be named.
  return (
    <div className={styles.cobrand}>
      <div className={styles.cobrandInner}>
        <div className={styles.eyebrow}>
          {bannerData.orgName
            ? t('rescue_banner_eyebrow', { name: bannerData.orgName })
            : t('rescue_banner_eyebrow_generic')}
        </div>
        <h3 className={styles.headline}>
          {/* personalized only while the adopter link's pet params are in the url; the generic line
              is the fallback everywhere else, including after navigation drops the query string */}
          {hasNanoVariantPetName()
            ? t('rescue_banner_headline_named', {
                petName: getNanoVariantPetName(),
                gender: getNanoVariantPetGenderSelect(),
              })
            : t('rescue_banner_headline')}
        </h3>
      </div>
    </div>
  );
}
